import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import AddressVerificationFields from "../components/common/AddressVerificationFields";
import API_BASE_URL from "../config";
import "./SalesPages.css";

function NewSalesLead() {
  const navigate = useNavigate();

  const [owners, setOwners] = useState([]);
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState("new");
  const [source, setSource] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [billingStatus, setBillingStatus] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");

  useEffect(() => {
    loadOwners();
    loadPlans();
  }, []);

  const loadOwners = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/users/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) return;

      const data = await res.json();
      setOwners(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load owners error:", err.message);
    }
  };

  const loadPlans = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) return;

      const data = await res.json();
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load plans error:", err.message);
    }
  };

  const addressValues = {
    business_address: businessAddress,
    city,
    state,
    zip_code: zipCode,
  };

  const handleAddressChange = (fieldName, value) => {
    if (fieldName === "business_address") setBusinessAddress(value);
    if (fieldName === "city") setCity(value);
    if (fieldName === "state") setState(value);
    if (fieldName === "zip_code") setZipCode(value);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/sales-leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          source,
          first_name: firstName,
          last_name: lastName,
          agency_name: agencyName,
          phone,
          email,
          website,
          business_address: businessAddress,
          city,
          state,
          zip_code: zipCode,
          subscription_status: subscriptionStatus,
          subscription_plan: subscriptionPlan,
          billing_status: billingStatus,
          owner_user_id: ownerUserId || null
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to create sales lead");

      navigate(`/sales-lead-profile/${data.id}`);
    } catch (err) {
      console.error("Create sales lead error:", err.message);
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">New Sales Lead</h1>
            <p className="sales-subtitle">
              Add a prospective agency and capture current subscription interest in one clean intake flow.
            </p>
          </div>

          <div className="sales-header-actions">
            <button className="sales-secondary-btn" onClick={() => navigate("/sales-leads")}>
              Cancel
            </button>
            <button className="sales-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Creating..." : "Create Sales Lead"}
            </button>
          </div>
        </div>

        <div className="sales-form-grid">
          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Lead Information</h2>
              <p>Basic prospect and agency details.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field">
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="sales-field">
                <label>Source</label>
                <input value={source} onChange={(e) => setSource(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>First Name</label>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Last Name</label>
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>

              <div className="sales-field sales-field-full">
                <label>Agency Name</label>
                <input value={agencyName} onChange={(e) => setAgencyName(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>

              <div className="sales-field">
                <label>Email Address</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>

              <div className="sales-field sales-field-full">
                <label>Website</label>
                <input value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>

              <AddressVerificationFields
                values={addressValues}
                onChange={handleAddressChange}
                streetLabel="Business Address"
                showUnit={false}
                showCounty={false}
                fieldMap={{
                  street: "business_address",
                  city: "city",
                  state: "state",
                  zip: "zip_code",
                  county: "county",
                  addressVerified: "address_verified",
                }}
              />

              <div className="sales-field">
                <label>Owner</label>
                <select value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)}>
                  <option value="">Select Owner</option>
                  {owners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name} {owner.email ? `(${owner.email})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Subscription Information</h2>
              <p>Track current subscription interest and billing stage.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field">
                <label>Subscription Status</label>
                <select value={subscriptionStatus} onChange={(e) => setSubscriptionStatus(e.target.value)}>
                  <option value="">Select</option>
                  <option value="not_started">Not Started</option>
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="past_due">Past Due</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="sales-field">
                <label>Subscription Plan</label>
                <select value={subscriptionPlan} onChange={(e) => setSubscriptionPlan(e.target.value)}>
                  <option value="">Select Plan</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.plan_key}>
                      {plan.plan_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sales-field">
                <label>Billing Status</label>
                <select value={billingStatus} onChange={(e) => setBillingStatus(e.target.value)}>
                  <option value="">Select</option>
                  <option value="pending">Pending</option>
                  <option value="current">Current</option>
                  <option value="past_due">Past Due</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewSalesLead;